import {
  Box,
  Container,
  Heading,
  Text,
  Stack,
  Divider,
  UnorderedList,
  ListItem,
  Link,
} from "@chakra-ui/react";

const LAST_UPDATED = "May 27, 2026";

const PrivacyPolicy = () => {
  return (
    <Box bg="gray.50" minH="100vh" py={10}>
      <Container
        maxW="4xl"
        shadow="md"
        bg="white"
        p={{ base: 6, md: 10 }}
        borderRadius="md"
      >
        <Stack spacing={6}>
          <Heading as="h1" size="xl">
            Privacy Policy
          </Heading>

          <Text color="gray.600">Last updated: {LAST_UPDATED}</Text>

          <Divider />

          <Text>
            This Privacy Policy applies to <strong>PickDengu</strong> and{" "}
            <strong>PickDengu Chat</strong>, available at{" "}
            <Link href="https://pickdengu.mw" color="blue.600" isExternal>
              https://pickdengu.mw
            </Link>
            . PickDengu is a WhatsApp-first commerce, delivery, booking,
            catalog, and business automation platform. PickDengu Chat helps
            businesses connect their Meta business portfolio, WhatsApp Business
            Account, WhatsApp phone numbers, product catalogs, customer
            messaging workflows, order notifications, booking confirmations, and
            delivery updates.
          </Text>

          <Text>
            This policy explains what information we collect, how we use it, how
            we share it, how long we keep it, and how users or connected
            businesses can request access, correction, deletion, or
            disconnection.
          </Text>

          <Heading as="h2" size="md">
            1. Who We Are
          </Heading>

          <Text>
            PickDengu and PickDengu Chat are operated by PickDengu. For privacy,
            data deletion, account disconnection, or support requests, contact:
          </Text>

          <UnorderedList pl={6}>
            <ListItem>
              Website:{" "}
              <Link href="https://pickdengu.mw" color="blue.600" isExternal>
                https://pickdengu.mw
              </Link>
            </ListItem>
            <ListItem>Email: support@pickdengu.mw</ListItem>
            <ListItem>App name used with Meta: PickDengu Chat</ListItem>
          </UnorderedList>

          <Heading as="h2" size="md">
            2. Services Covered by This Policy
          </Heading>

          <Text>
            This policy applies to PickDengu websites, dashboards, WhatsApp
            flows, Meta integrations, product catalog integrations, delivery
            tools, order management tools, booking tools, car hire tools,
            accommodation tools, customer support features, and related business
            automation services.
          </Text>

          <Heading as="h2" size="md">
            3. Information We Collect
          </Heading>

          <Text>Depending on how you use PickDengu, we may collect:</Text>

          <UnorderedList pl={6}>
            <ListItem>
              Identity information such as name, display name, business name,
              user ID, seller ID, driver ID, or account ID.
            </ListItem>
            <ListItem>
              Contact information such as email address, phone number, WhatsApp
              number, and delivery contact details.
            </ListItem>
            <ListItem>
              Account information such as login method, Firebase user ID, role,
              permissions, and review/test access status.
            </ListItem>
            <ListItem>
              Order and transaction information such as cart items, order IDs,
              payment status, invoices, refunds, delivery fees, seller
              allocations, driver jobs, and payout records.
            </ListItem>
            <ListItem>
              Delivery and location information such as pickup address, delivery
              address, warehouse location, route distance, delivery status, and
              proof-of-delivery information.
            </ListItem>
            <ListItem>
              Booking information for hikes/trips, accommodation, car hire, and
              related reservation services.
            </ListItem>
            <ListItem>
              Communications information such as WhatsApp messages, template
              names, delivery responses, message IDs, support messages, and
              notification history.
            </ListItem>
            <ListItem>
              Product and catalog information such as product IDs, catalog IDs,
              product names, prices, availability, product images, descriptions,
              variants, and seller product records.
            </ListItem>
            <ListItem>
              Technical information such as IP address, browser type, device
              type, pages visited, timestamps, error logs, and API request logs.
            </ListItem>
          </UnorderedList>

          <Heading as="h2" size="md">
            4. Meta, Facebook, WhatsApp and Catalog Data
          </Heading>

          <Text>
            When a business connects PickDengu Chat through Meta Login or Meta
            Business tools, PickDengu may receive access to information that the
            business authorizes through Meta. This may include:
          </Text>

          <UnorderedList pl={6}>
            <ListItem>Meta business portfolio identifiers.</ListItem>
            <ListItem>Business account names and business asset IDs.</ListItem>
            <ListItem>WhatsApp Business Account IDs.</ListItem>
            <ListItem>WhatsApp phone number IDs.</ListItem>
            <ListItem>
              WhatsApp display phone numbers, verified names, account review
              status, and quality rating.
            </ListItem>
            <ListItem>
              Approved WhatsApp message template names, language codes, message
              send responses, message IDs, and delivery responses.
            </ListItem>
            <ListItem>
              Product catalog IDs, catalog names, product IDs, retailer IDs,
              product names, prices, availability, descriptions, images, and
              related catalog metadata.
            </ListItem>
          </UnorderedList>

          <Text>
            PickDengu uses Meta, Facebook, WhatsApp, and catalog data only to
            provide services requested by the connected business, including
            WhatsApp-first commerce, order notifications, booking confirmations,
            delivery updates, customer support, business automation, and product
            catalog synchronization.
          </Text>

          <Text>
            PickDengu does not sell Meta, WhatsApp, catalog, or customer
            messaging data.
          </Text>

          <Heading as="h2" size="md">
            5. Meta Permissions Used by PickDengu Chat
          </Heading>

          <Text>
            PickDengu Chat may request Meta permissions so that businesses can
            connect and manage their commerce and messaging workflows. These may
            include:
          </Text>

          <UnorderedList pl={6}>
            <ListItem>
              <strong>email</strong>: to receive the email address associated
              with the connected Meta user where authorized.
            </ListItem>
            <ListItem>
              <strong>business_management</strong>: to connect and manage
              business portfolio assets that the business authorizes.
            </ListItem>
            <ListItem>
              <strong>whatsapp_business_management</strong>: to read and manage
              WhatsApp Business Account and phone number information for the
              connected business.
            </ListItem>
            <ListItem>
              <strong>whatsapp_business_messaging</strong>: to send approved
              WhatsApp templates and business messages for order, booking,
              delivery, payment, and customer support workflows.
            </ListItem>
            <ListItem>
              <strong>catalog_management</strong>: to read, create, update,
              display, and synchronize products in the connected business
              catalog.
            </ListItem>
          </UnorderedList>

          <Heading as="h2" size="md">
            6. How We Use Information
          </Heading>

          <Text>We use information to:</Text>

          <UnorderedList pl={6}>
            <ListItem>
              Provide and operate PickDengu and PickDengu Chat.
            </ListItem>
            <ListItem>Create, manage, and authenticate user accounts.</ListItem>
            <ListItem>
              Process orders, bookings, invoices, and payments.
            </ListItem>
            <ListItem>
              Coordinate delivery, dispatch, tracking, and proof of delivery.
            </ListItem>
            <ListItem>
              Send WhatsApp notifications, templates, support responses, and
              operational updates.
            </ListItem>
            <ListItem>
              Connect WhatsApp Business Accounts, phone numbers, business
              portfolios, and product catalogs.
            </ListItem>
            <ListItem>
              Read, display, create, update, and synchronize catalog products.
            </ListItem>
            <ListItem>
              Prevent fraud, abuse, unauthorized access, and misuse.
            </ListItem>
            <ListItem>
              Improve reliability, security, support, and user experience.
            </ListItem>
            <ListItem>
              Comply with legal, tax, regulatory, and platform obligations.
            </ListItem>
          </UnorderedList>

          <Heading as="h2" size="md">
            7. WhatsApp Messaging and Consent
          </Heading>

          <Text>
            PickDengu may send WhatsApp messages when users or businesses
            interact with PickDengu through WhatsApp, place orders, make
            bookings, request delivery, request support, or opt in to receive
            messages.
          </Text>

          <Text>
            WhatsApp messages may include order confirmations, payment
            confirmations, booking confirmations, delivery updates, driver
            updates, pickup or drop-off information, verification codes,
            invoices, reminders, support messages, and business service
            messages.
          </Text>

          <Text>
            You may opt out of non-essential WhatsApp communications by replying{" "}
            <strong>STOP</strong> or contacting support@pickdengu.mw. Some
            transactional or security messages may still be sent where necessary
            to complete a requested service.
          </Text>

          <Heading as="h2" size="md">
            8. Catalog and Product Data
          </Heading>

          <Text>
            Businesses may connect product catalogs to PickDengu. PickDengu may
            read, display, create, update, and synchronize product information
            such as product names, prices, availability, images, descriptions,
            variants, and retailer IDs. This allows businesses to manage
            commerce workflows across PickDengu, WhatsApp, and Meta catalog
            tools.
          </Text>

          <Heading as="h2" size="md">
            9. How We Share Information
          </Heading>

          <Text>
            We do not sell personal information. We may share information only
            where necessary to provide the services, comply with law, or operate
            integrations requested by the user or connected business.
          </Text>

          <UnorderedList pl={6}>
            <ListItem>
              With Meta Platforms, Inc. and WhatsApp where required to operate
              Meta Login, WhatsApp Business messaging, WhatsApp Business
              management, and catalog integrations.
            </ListItem>
            <ListItem>
              With sellers, businesses, drivers, warehouses, or service
              providers where necessary to fulfill orders, delivery, bookings,
              payouts, and support.
            </ListItem>
            <ListItem>
              With payment, hosting, database, analytics, email, security,
              messaging, and infrastructure providers that help us operate the
              platform.
            </ListItem>
            <ListItem>
              With legal, regulatory, tax, law-enforcement, or safety
              authorities where required by law or necessary to protect rights,
              safety, and security.
            </ListItem>
          </UnorderedList>

          <Heading as="h2" size="md">
            10. Data Retention
          </Heading>

          <Text>
            We keep information only for as long as needed to provide services,
            maintain business records, resolve disputes, comply with legal and
            tax obligations, prevent fraud, and operate secure platform
            integrations. Retention periods may vary depending on the type of
            information and the service involved.
          </Text>

          <Text>
            Meta-connected tokens, catalog data, WhatsApp account data, and
            integration records may be retained while a business remains
            connected to PickDengu Chat or while needed for operational,
            security, audit, or legal reasons.
          </Text>

          <Heading as="h2" size="md">
            11. Data Security
          </Heading>

          <Text>
            We use reasonable administrative, technical, and organizational
            safeguards designed to protect information against unauthorized
            access, loss, misuse, alteration, or disclosure. However, no system
            is completely secure, and we cannot guarantee absolute security.
          </Text>

          <Heading as="h2" size="md">
            12. Your Choices and Rights
          </Heading>

          <Text>
            Depending on your location and applicable law, you may have rights
            to request access, correction, deletion, restriction, objection, or
            portability of your personal information.
          </Text>

          <Text>
            To make a request, contact support@pickdengu.mw. We may need to
            verify your identity or authority before completing the request.
          </Text>

          <Heading as="h2" size="md">
            13. Meta Connection, Disconnection and Data Deletion
          </Heading>

          <Text>
            A business can disconnect PickDengu Chat from Meta Business
            Integrations at any time through Meta/Facebook settings. Users or
            businesses may also request that PickDengu disconnect their Meta
            assets or delete Meta-connected data by contacting
            support@pickdengu.mw.
          </Text>

          <Text>
            To request deletion of PickDengu account data, Meta-connected data,
            WhatsApp-connected data, catalog-connected data, or business
            integration data, email support@pickdengu.mw with the subject{" "}
            <strong>Data Deletion Request</strong>. Include the account email,
            business name, phone number, and the data you want deleted or
            disconnected.
          </Text>

          <Text>
            We will process valid deletion or disconnection requests subject to
            legal, tax, fraud prevention, security, and operational retention
            requirements.
          </Text>

          <Heading as="h2" size="md">
            14. International Data Transfers
          </Heading>

          <Text>
            PickDengu may process information using service providers or
            infrastructure located in different countries. Where required, we
            take steps designed to protect information in accordance with this
            policy and applicable law.
          </Text>

          <Heading as="h2" size="md">
            15. Children’s Privacy
          </Heading>

          <Text>
            PickDengu is not intended for children under the age required by
            applicable law to use online commerce or messaging services. We do
            not knowingly collect personal information from children where
            parental or guardian consent is required.
          </Text>

          <Heading as="h2" size="md">
            16. Third-Party Links and Services
          </Heading>

          <Text>
            PickDengu may link to third-party websites or services, including
            Meta, WhatsApp, payment providers, delivery tools, map providers,
            seller stores, and business tools. Their privacy practices are
            governed by their own policies.
          </Text>

          <Heading as="h2" size="md">
            17. Changes to This Privacy Policy
          </Heading>

          <Text>
            We may update this Privacy Policy from time to time. The updated
            version will be posted on this page with a new “Last updated” date.
          </Text>

          <Heading as="h2" size="md">
            18. Contact Us
          </Heading>

          <Text>
            For questions, privacy requests, data deletion, Meta disconnection,
            or support, contact:
          </Text>

          <UnorderedList pl={6}>
            <ListItem>Email: support@pickdengu.mw</ListItem>
            <ListItem>
              Website:{" "}
              <Link href="https://pickdengu.mw" color="blue.600" isExternal>
                https://pickdengu.mw
              </Link>
            </ListItem>
          </UnorderedList>

          <Divider />

          <Text fontSize="sm" color="gray.500">
            © {new Date().getFullYear()} PickDengu. All rights reserved.
          </Text>
        </Stack>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
